<template>
  <section>
    <!-- Payment Method Tabs -->
    <v-tabs
      v-model="localPaymentTab"
      grow
      height="60"
      slider-color="primary"
      @change="onTabChange"
    >
      <v-tab
        v-for="method in paymentList"
        :key="method.value"
        :value="method.value"
        class="payment-tab"
        @click="onTabClick(method)"
      >
        <span class="payment-label">{{ $t(method.name) }}</span>
      </v-tab>
    </v-tabs>
    <template v-if="!isQRActive">
      <NumericKeyboard
        :type="store_type"
        v-model="localPayAmount"
        :totalPrice="finalTotalPrice"
        :currencyData="defineStore.currency"
        @customer="makeCustomer"
        @submit="makeSubmit"
        @openNoted="openNoted"
        @exchangeRate="exchangeRate"
      />
    </template>
    <template v-else>
      <!-- Cash Payment -->
      <v-card v-if="localPaymentTab === 'cash'" flat>
        <NumericKeyboard
          :type="store_type"
          v-model="localPayAmount"
          :totalPrice="finalTotalPrice"
          :currencyData="defineStore.currency"
          @customer="makeCustomer"
          @submit="makeSubmit"
          @openNoted="openNoted"
          @exchangeRate="exchangeRate"
        />
      </v-card>
      <!-- Transfer Payment -->
      <v-card v-else-if="localPaymentTab === 'transfer'" flat>
        <div class="pa-4">
          <v-alert type="info" dense text class="mb-4">
            {{ $t("transfer_payment_info") }}
          </v-alert>
          <v-text-field
            v-model="transferReference"
            :label="$t('transfer_reference')"
            :placeholder="$t('enter_transfer_reference')"
            outlined
            dense
            clearable
            class="mb-4"
          />
          <NumericKeyboard
            :type="store_type"
            v-model="localPayAmount"
            :totalPrice="finalTotalPrice"
            :currencyData="defineStore.currency"
            @customer="makeCustomer"
            @submit="makeSubmit"
            @openNoted="openNoted"
            @exchangeRate="exchangeRate"
          />
        </div>
      </v-card>
      <!-- Credit Payment -->
      <v-card v-else-if="localPaymentTab === 'credit'" flat>
        <div class="pa-4">
          <v-alert type="info" dense text class="mb-4">
            {{ $t("credit_payment_info") }}
          </v-alert>
          <v-text-field
            v-model="creditCardNumber"
            :label="$t('credit_card_number')"
            :placeholder="$t('enter_credit_card_number')"
            outlined
            dense
            clearable
            class="mb-4"
          />
          <NumericKeyboard
            :type="store_type"
            v-model="localPayAmount"
            :totalPrice="finalTotalPrice"
            :currencyData="defineStore.currency"
            @customer="makeCustomer"
            @submit="makeSubmit"
            @openNoted="openNoted"
            @exchangeRate="exchangeRate"
          />
        </div>
      </v-card>
      <!-- Cash with Transfer Payment -->
      <v-card v-else-if="localPaymentTab === 'cash_with_transfer'" flat>
        <div class="pa-4">
          <v-alert type="info" dense text class="mb-4">
            {{ $t("cash_with_transfer_info") }}
          </v-alert>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="cashAmount"
                :label="$t('cash_amount')"
                :placeholder="$t('enter_cash_amount')"
                outlined
                dense
                type="number"
                class="mb-4"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="transferAmount"
                :label="$t('transfer_amount')"
                :placeholder="$t('enter_transfer_amount')"
                outlined
                dense
                type="number"
                class="mb-4"
              />
            </v-col>
          </v-row>
          <v-text-field
            v-model="transferReference"
            :label="$t('transfer_reference')"
            :placeholder="$t('enter_transfer_reference')"
            outlined
            dense
            clearable
            class="mb-4"
          />
          <NumericKeyboard
            :type="store_type"
            v-model="localPayAmount"
            :totalPrice="finalTotalPrice"
            :currencyData="defineStore.currency"
            @customer="makeCustomer"
            @submit="makeSubmit"
            @openNoted="openNoted"
            @exchangeRate="exchangeRate"
          />
        </div>
      </v-card>
      <!-- QR Payment -->
      <v-card v-else-if="localPaymentTab === 'qr'" flat>
        <div class="pa-3">
          <!-- Skeleton Loading State - Show when QR tab is selected and loading -->
          <div v-if="qrLoading" class="text-center pa-5">
            <!-- Skeleton for QR Code -->
            <div class="skeleton-qr-container mb-4">
              <v-skeleton-loader
                type="image"
                width="200"
                height="200"
                class="mx-auto"
              ></v-skeleton-loader>
            </div>

            <!-- Skeleton for Price Amount -->
            <div class="skeleton-price-section">
              <v-skeleton-loader
                type="text"
                width="150"
                class="mx-auto mb-2"
              ></v-skeleton-loader>
              <v-skeleton-loader
                type="text"
                width="200"
                class="mx-auto mb-3"
              ></v-skeleton-loader>
              <v-skeleton-loader
                type="text"
                width="180"
                class="mx-auto mb-3"
              ></v-skeleton-loader>
            </div>
          </div>

          <!-- QR Code Display - Only show when waiting for payment -->
          <div v-else-if="qrCodeData" class="text-center">
            <div class="text-h6 mb-3">{{ $t("scan_qr_to_pay") }}</div>

            <!-- QR Code Image -->
            <div class="qr-container mb-4">
              <div class="qr-wrapper">
                <img :src="qrCodeData.qrCode" alt="QR Code" class="qr-image" />
                <div class="qr-image-wrapper"></div>
                <div class="qr-overlay">
                  <img
                    src="/images/lao_qr.png"
                    alt="Lao QR"
                    class="lao-qr-overlay"
                    @error="handleLaoQRError"
                  />
                </div>
              </div>
            </div>

            <!-- Price Amount Below QR -->
            <div class="price-amount-section">
              <div class="text-h5 font-weight-bold primary--text mb-2">
                {{ qrCodeData.amount | NumberFormat }}
              </div>
              <div class="text-body-2 grey--text mb-3">
                {{ qrCodeData.terminal_label }}
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="d-flex justify-center gap-2 mb-4">
              <v-btn
                small
                outlined
                color="primary"
                @click="openManualPaymentDialog"
              >
                <v-icon left small>fal fa-edit</v-icon>
                {{ $t("manual_confirmation") }}
              </v-btn>
              <v-btn small outlined color="error" @click="switchToCashPayment">
                <v-icon left small>fal fa-money-bill</v-icon>
                {{ $t("switch_to_cash") }}
              </v-btn>
            </div>
          </div>

          <!-- Generate QR Button - Show when no QR data -->
          <div v-else class="text-center pa-5">
            <v-btn
              color="primary"
              large
              @click="submitPayment"
              :loading="qrLoading"
            >
              <v-icon left>fal fa-qrcode</v-icon>
              {{ $t("generate_qr_code") }}
            </v-btn>
          </div>
        </div>
      </v-card>
      <!-- COD Payment -->
      <v-card v-else-if="localPaymentTab === 'cod'" flat>
        <div class="pa-4">
          <v-alert type="info" dense text class="mb-4">
            {{ $t("cod_payment_info") }}
          </v-alert>
          <NumericKeyboard
            :type="store_type"
            v-model="localPayAmount"
            :totalPrice="finalTotalPrice"
            :currencyData="defineStore.currency"
            @customer="makeCustomer"
            @submit="makeSubmit"
            @openNoted="openNoted"
            @exchangeRate="exchangeRate"
          />
        </div>
      </v-card>
    </template>

    <!-- Manual Payment Confirmation Dialog -->
    <app-custom-dialog
      v-model="showManualPaymentDialog"
      title="manual_payment_confirmation"
      width="450px"
      cancel-text="cancel"
      submit-text="confirm"
      :submit-loading="manualPaymentLoading"
      @submit="confirmManualPayment"
    >
      <div slot="activator">
          <div class="manual-payment-section">
            <v-alert type="info" dense outlined class="mb-4">
              <div class="text-body-2">
                {{ $t("manual_payment_info") }}
              </div>
            </v-alert>

            <div class="payment-details mb-4">
              <div class="text-subtitle-1 font-weight-bold mb-2">
                {{ $t("payment_details") }}
              </div>
              <div class="text-body-2 grey--text mb-1">
                {{ $t("bill_number") }}: {{ qrCodeData?.ticket_id }}
              </div>
              <div class="text-body-2 grey--text mb-1">
                {{ $t("amount") }}: {{ qrCodeData?.amount | NumberFormat }}
              </div>
            </div>

            <v-text-field
              v-model="manualPaymentReference"
              :label="$t('payment_reference')"
              :placeholder="$t('enter_payment_reference')"
              :error-messages="manualPaymentErrors"
              outlined
              dense
              required
              clearable
            />

            <div class="text-caption grey--text mt-2">
              {{ $t("manual_payment_reference_hint") }}
            </div>
          </div>
      </div>
    </app-custom-dialog>
  </section>
</template>

<script>
import NumericKeyboard from "@/components/NumericKeyboard";
import { toast } from "@/controllers/alert";
import { myMixin } from "@/mixin/main";
import { mainStore } from "@/stores/modules/cart";

export default {
  name: "SaleNumPad",
  components: {
    NumericKeyboard,
  },
  mixins: [myMixin],
  props: {
    paymentTab: {
      type: String,
      default: "cash",
    },
    pay_amount: {
      type: [String, Number],
      default: "",
    },
  },
  data() {
    return {
      defineStore: mainStore(),
      qrLoading: false,
      qrCodeData: null,
      manualPaymentLoading: false,
      manualPaymentReference: "",
      manualPaymentErrors: [],
      showManualPaymentDialog: false,
      // New payment method properties
      transferReference: "",
      creditCardNumber: "",
      cashAmount: "",
      transferAmount: "",
    };
  },
  computed: {
    isQRActive() {
      // Check if user and stores exist and QR is active
      return (
        this.user && this.user.stores && this.user.stores.is_active_qr === 1
      );
    },

    // Calculate final total price after discount
    finalTotalPrice() {
      const { total_price, total_discount } = this.defineStore;
      const finalPrice = total_price - total_discount;
      return finalPrice;
    },

    // Computed property for two-way binding of paymentTab
    localPaymentTab: {
      get() {
        // Always ensure we return a string, default to "cash"
        const currentTab = this.paymentTab;
        console.log("🔍 [SaleNumPad] localPaymentTab GET:", {
          currentTab,
          type: typeof currentTab,
          paymentList: this.paymentList,
          paymentListLength: this.paymentList?.length,
        });

        if (typeof currentTab === "string") {
          console.log("✅ [SaleNumPad] Returning string value:", currentTab);
          return currentTab;
        }
        // If it's a number (index), convert to corresponding payment method value
        if (
          typeof currentTab === "number" &&
          this.paymentList &&
          this.paymentList[currentTab]
        ) {
          const convertedValue = this.paymentList[currentTab].value;
          console.log("✅ [SaleNumPad] Converting number index to value:", {
            index: currentTab,
            value: convertedValue,
          });
          return convertedValue;
        }
        // Default fallback
        console.log("⚠️ [SaleNumPad] Using default fallback: cash");
        return "cash";
      },
      set(value) {
        console.log("🔍 [SaleNumPad] localPaymentTab SET called:", {
          value,
          type: typeof value,
          paymentList: this.paymentList,
        });

        // Convert number index to string value if needed
        let emitValue;
        if (typeof value === "number") {
          // If value is an index, get the actual payment method value
          if (this.paymentList && this.paymentList[value]) {
            emitValue = this.paymentList[value].value;
            console.log("✅ [SaleNumPad] Converted number index to value:", {
              index: value,
              value: emitValue,
            });
          } else {
            emitValue = "cash"; // Fallback if index is out of range
            console.log(
              "⚠️ [SaleNumPad] Index out of range, using fallback:",
              emitValue
            );
          }
        } else if (typeof value === "string") {
          emitValue = value;
          console.log("✅ [SaleNumPad] Using string value as-is:", emitValue);
        } else {
          emitValue = String(value || "cash");
          console.log("⚠️ [SaleNumPad] Converting to string:", emitValue);
        }
        console.log(
          "📤 [SaleNumPad] Emitting update:paymentTab with value:",
          emitValue
        );
        this.$emit("update:paymentTab", emitValue);
      },
    },

    // Computed property for two-way binding of pay_amount
    localPayAmount: {
      get() {
        return this.pay_amount;
      },
      set(value) {
        this.$emit("update:pay_amount", value);
      },
    },
    paymentList() {
      // check if store is cod
      const list = this.defineStore.paymentList;
      console.log("🔍 [SaleNumPad] paymentList computed:", {
        hasUser: !!this.user,
        hasStores: !!(this.user && this.user.stores),
        isCod: this.user?.stores?.is_cod,
        originalList: list,
        originalListLength: list?.length,
      });

      if (this.user && this.user.stores && !this.user.stores.is_cod) {
        const filtered = list.filter((item) => item.value !== "cod");
        console.log(
          "✅ [SaleNumPad] Filtered paymentList (removed COD):",
          filtered
        );
        return filtered;
      }
      console.log("✅ [SaleNumPad] Returning full paymentList:", list);
      return list;
    },
  },
  watch: {
    localPaymentTab: {
      handler(val, oldVal) {
        console.log("🔍 [SaleNumPad] localPaymentTab WATCH triggered:", {
          newValue: val,
          oldValue: oldVal,
          type: typeof val,
          isQRActive: this.isQRActive,
          paymentTab: this.paymentTab,
        });

        // Auto-generate QR when QR tab is selected and QR is active
        if (val === "qr" && this.isQRActive) {
          console.log("✅ [SaleNumPad] QR tab selected, generating QR...");
          // Show skeleton loading immediately
          this.qrLoading = true;
          // Generate QR after a short delay to show skeleton
          setTimeout(() => {
            this.submitPayment();
          }, 100);
        } else if (val === "qr" && !this.isQRActive) {
          console.log("⚠️ [SaleNumPad] QR tab selected but QR is not active");
          // If QR is not active, don't show loading or generate QR
          this.qrLoading = false;
          this.qrCodeData = null;
        } else {
          console.log(
            "✅ [SaleNumPad] Non-QR tab selected, resetting QR state"
          );
          // Reset loading state when switching to other tabs
          this.qrLoading = false;
          this.qrCodeData = null;
        }
      },
      immediate: true,
    },
    paymentTab: {
      handler(newVal, oldVal) {
        console.log("🔍 [SaleNumPad] paymentTab (prop) WATCH triggered:", {
          newValue: newVal,
          oldValue: oldVal,
          type: typeof newVal,
        });
      },
      immediate: true,
    },
  },
  mounted() {
    console.log("🚀 [SaleNumPad] Component mounted:", {
      paymentTab: this.paymentTab,
      paymentTabType: typeof this.paymentTab,
      paymentList: this.paymentList,
      localPaymentTab: this.localPaymentTab,
      localPaymentTabType: typeof this.localPaymentTab,
      defineStorePaymentList: this.defineStore.paymentList,
      user: this.user,
    });
  },
  methods: {
    // Debug methods for tab clicking
    onTabClick(method) {
      console.log("🖱️ [SaleNumPad] Tab clicked:", {
        method,
        methodValue: method.value,
        currentLocalPaymentTab: this.localPaymentTab,
        currentPaymentTab: this.paymentTab,
      });
    },
    onTabChange(value) {
      console.log("🔄 [SaleNumPad] Tab change event:", {
        value,
        type: typeof value,
        currentLocalPaymentTab: this.localPaymentTab,
        currentPaymentTab: this.paymentTab,
      });
    },
    // Emit functions to parent component
    makeCustomer() {
      this.$emit("customer");
    },

    makeSubmit() {
      this.$emit("submit");
      this.qrLoading = false;
      this.qrCodeData = null;
      this.localPaymentTab = "cash";
    },

    openNoted() {
      this.$emit("openNoted");
    },

    exchangeRate() {
      this.$emit("exchangeRate");
    },

    switchToCashPayment() {
      // Emit event to parent to handle paymentTab change
      this.$emit("switchToCash");
    },

    async submitPayment() {
      if (!this.isQRActive) {
        return;
      }

      // Prepare payment data based on payment method
      let paymentData = {
        type: this.localPaymentTab,
        status: "WAITING_PAYMENT",
      };

      // Add additional data for specific payment methods
      if (this.localPaymentTab === "transfer" && this.transferReference) {
        paymentData.reference = this.transferReference;
      } else if (this.localPaymentTab === "credit" && this.creditCardNumber) {
        paymentData.cardNumber = this.creditCardNumber;
      } else if (this.localPaymentTab === "cash_with_transfer") {
        paymentData.cashAmount = this.cashAmount;
        paymentData.transferAmount = this.transferAmount;
        paymentData.reference = this.transferReference;
      }

      this.$emit("submitPayment", paymentData);
    },

    openManualPaymentDialog() {
      this.showManualPaymentDialog = true;
      this.manualPaymentReference = "";
      this.manualPaymentErrors = [];
    },

    async confirmManualPayment() {
      // Validate reference input
      if (
        !this.manualPaymentReference ||
        this.manualPaymentReference.trim() === ""
      ) {
        this.manualPaymentErrors = [this.$t("payment_reference_required")];
        return;
      }

      this.manualPaymentErrors = [];
      this.manualPaymentLoading = true;

      try {
        const response = await this.$http.post("manual_payment_confirmation", {
          ticket_id: this.qrCodeData.ticket_id,
          reference: this.manualPaymentReference.trim(),
          amount: this.qrCodeData.amount,
        });

        if (response.data && response.data.status) {
          // Success - close dialog and show success message
          this.showManualPaymentDialog = false;
          this.manualPaymentReference = "";

          // Emit success event to parent
          this.$emit("manualPaymentSuccess", response.data.data);
        } else {
          toast(
            this,
            response.data?.message || "manual_payment_failed",
            "error"
          );
        }
      } catch (error) {
        console.error("Error confirming manual payment:", error);
        if (error.response && error.response.data) {
          toast(
            this,
            error.response.data.message || "manual_payment_failed",
            "error"
          );
        } else {
          toast(this, "manual_payment_failed", "error");
        }
      } finally {
        this.manualPaymentLoading = false;
      }
    },

    handleLaoQRError() {
      console.error("Lao QR image failed to load. Using a placeholder.");
    },

    async downloadQRCardAsImage() {
      this.$emit("downloadQR", this.qrCodeData);
    },

    // Method to set QR data from parent
    setQRData(qrData) {
      this.qrCodeData = qrData;
      this.qrLoading = false;
    },

    // Method to set loading state from parent
    setLoading(loading) {
      this.qrLoading = loading;
    },

    // Method to reset QR state
    resetQR() {
      this.qrCodeData = null;
      this.qrLoading = false;
      this.manualPaymentReference = "";
      this.manualPaymentErrors = [];
      this.showManualPaymentDialog = false;
    },
  },
};
</script>

<style scoped>
.qr-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.qr-wrapper {
  position: relative;
  width: 200px;
  height: 200px;
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
}

.qr-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.qr-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
}

.lao-qr-overlay {
  width: 25px;
  height: 25px;
  object-fit: contain;
  background-color: #fff;
  border-radius: 4px;
  padding: 4px;
}

.price-amount-section {
  padding: 16px;
  background-color: #f5f5f5;
  border-radius: 8px;
  margin-top: 16px;
}

.skeleton-qr-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

.skeleton-price-section {
  padding: 16px;
  background-color: #f5f5f5;
  border-radius: 8px;
  margin-top: 16px;
}

.v-tabs {
  margin-bottom: 16px;
}

.v-window {
  min-height: 400px;
}

.payment-tab {
  text-transform: none;
  font-weight: 600;
  min-width: 100px;
}

.payment-label {
  font-size: 13px;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .payment-tab {
    min-width: 80px;
    font-size: 12px;
  }

  .payment-label {
    font-size: 11px;
  }
}

/* Manual Payment Dialog Styles */
.manual-payment-section {
  padding: 16px 0;
}

.payment-details {
  background-color: #f8f9fa;
  padding: 16px;
  border-radius: 8px;
  border-left: 4px solid #2196f3;
}

.manual-payment-section .v-alert {
  border-radius: 8px;
}

.manual-payment-section .v-text-field {
  margin-bottom: 8px;
}
</style>
